export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#1A2E1C]">{title}</h1>
        {subtitle && <p className="mt-1 text-[#5C6B5E]">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Card({ className = "", children, ...rest }) {
  return (
    <div
      className={`bg-white border border-[#E5E2DC] rounded-xl shadow-sm p-6 ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function StatCard({ label, value, hint, accent = "moss", onClick, testid }) {
  const accentClasses = {
    moss: "bg-[#A3B18A]/20 text-[#1E3F20]",
    ochre: "bg-[#DDA15E]/25 text-[#8B5A15]",
    terra: "bg-[#E07A5F]/20 text-[#B44A2D]",
    forest: "bg-[#1E3F20]/15 text-[#1E3F20]",
  };
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      {...(clickable ? { type: "button", onClick } : {})}
      data-testid={testid}
      className={`bg-white border border-[#E5E2DC] rounded-xl p-5 transition-all text-left w-full ${
        clickable
          ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-[#1E3F20]/40 focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
          : "hover:-translate-y-0.5 hover:shadow-md"
      }`}
    >
      <div className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${accentClasses[accent]}`}>
        {label}
      </div>
      <div className="mt-3 font-heading text-3xl font-black text-[#1A2E1C]">{value}</div>
      {hint && <div className="mt-1 text-xs text-[#5C6B5E]">{hint}</div>}
    </Wrapper>
  );
}

export function Button({ variant = "primary", className = "", ...rest }) {
  const styles = {
    primary: "bg-[#1E3F20] text-white hover:bg-[#2C5530]",
    secondary: "bg-[#E07A5F] text-white hover:bg-[#CC5500]",
    outline: "bg-transparent text-[#1E3F20] border border-[#1E3F20] hover:bg-[#F2EDE4]",
    ghost: "bg-transparent text-[#1A2E1C] hover:bg-[#F2EDE4]",
  };
  return (
    <button
      {...rest}
      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-sm transition-colors disabled:opacity-60 ${styles[variant]} ${className}`}
    />
  );
}

export function Input({ className = "", ...rest }) {
  return (
    <input
      {...rest}
      className={`w-full border border-[#E5E2DC] bg-white rounded-lg px-3.5 py-2.5 text-[#1A2E1C] placeholder-[#5C6B5E] focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent text-sm ${className}`}
    />
  );
}

export function Textarea({ className = "", ...rest }) {
  return (
    <textarea
      {...rest}
      className={`w-full border border-[#E5E2DC] bg-white rounded-lg px-3.5 py-2.5 text-[#1A2E1C] placeholder-[#5C6B5E] focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent text-sm ${className}`}
    />
  );
}

export function Select({ className = "", children, ...rest }) {
  return (
    <select
      {...rest}
      className={`w-full border border-[#E5E2DC] bg-white rounded-lg px-3 py-2.5 text-[#1A2E1C] focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent text-sm ${className}`}
    >
      {children}
    </select>
  );
}

export function Label({ children, className = "" }) {
  return (
    <label className={`text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C6B5E] mb-1.5 block ${className}`}>
      {children}
    </label>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending: "bg-[#DDA15E]/20 text-[#8B5A15]",
    approved: "bg-[#A3B18A]/30 text-[#1E3F20]",
    dispatched: "bg-[#1E3F20]/15 text-[#1E3F20]",
    overdue: "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

export function EmptyState({ title, description }) {
  return (
    <div className="border border-dashed border-[#E5E2DC] rounded-xl p-10 text-center bg-white/60">
      <div className="font-heading text-lg font-bold text-[#1A2E1C]">{title}</div>
      {description && <p className="mt-1 text-sm text-[#5C6B5E]">{description}</p>}
    </div>
  );
}
